import React from 'react';
import { useTranslation } from 'react-i18next';
import { centsToEuros } from '../../utils/moneyUtils';
import { formatCurrency, DATE_LOCALES } from '../../utils/formatters';

/**
 * `common/Amount` — the FaeBill "Buchungszahl" signature primitive
 * (M-D5-5, `architecture-ux-design-v5.md` §3a/§2.3).
 *
 * Renders a money amount in the Data-Face (`font-data tabular-nums`, Spline
 * Sans Mono) with de-DE-convention layout ("1.234,56 €", €-symbol AFTER the
 * number). By default (size='display') the fractional cents + comma are
 * visually de-emphasized (0.7em, `--text-muted`) so the Euro integer part
 * dominates — the "Hauptbuch-Blick" read. Negative amounts use the real
 * minus sign (U+2212), never the ASCII hyphen.
 *
 * Rounding/cent-math is delegated to {@link centsToEuros} (`utils/moneyUtils`)
 * and display formatting to {@link formatCurrency} (`utils/formatters`) —
 * this component never invents its own cent arithmetic.
 */
export interface AmountProps {
  /** Integer cents. Sign is semantic (caller decides negative/positive meaning). */
  cents: number;
  /** v5: only EUR is supported — the € symbol is still resolved via `Intl`, not hardcoded. */
  currency?: 'EUR';
  /** 14 / 20 / 28px (Typo-Skala §2.2). Default `'md'`. */
  size?: 'sm' | 'md' | 'display';
  tone?: 'default' | 'success' | 'danger' | 'muted';
  /** Default `true` when `size==='display'`, otherwise `false`. */
  deemphasizeCents?: boolean;
  className?: string;
  /** Screen reader text. Always reads the FULL, un-abridged amount — the
   * visual de-emphasis of cents is purely presentational. Defaults to the
   * full `formatCurrency` output when omitted. */
  'aria-label'?: string;
}

/** Real minus (U+2212) — never the ASCII hyphen-minus (U+002D). */
const REAL_MINUS = String.fromCharCode(0x2212);

/** Narrow no-break space between the number and the €-symbol (spec §3a). */
const NARROW_SPACE = ' ';

/**
 * `size` → Tailwind class(es). `sm`/`display` map onto the M-D5-1 Typo-Skala
 * aliases (§2.2, exact 14px/28px token matches); `md` (20px) has no matching
 * token in the scale, so it stays a literal Tailwind arbitrary-value class.
 */
const SIZE_CLASSES: Record<NonNullable<AmountProps['size']>, string> = {
  sm: 'text-body',
  md: 'text-[20px] leading-[26px]',
  display: 'text-display',
};

/** `tone` → text-color class. `default` uses the primary readable ink
 * (`--text-primary`) — an un-getönter Betrag is the primary metric and must
 * stay legible on dark cards, not silently inherit the ambient text color. */
const TONE_CLASSES: Record<NonNullable<AmountProps['tone']>, string> = {
  default: 'text-primary',
  success: 'text-[var(--tone-success-fg)]',
  danger: 'text-[var(--tone-danger-fg)]',
  muted: 'text-muted',
};

interface SplitAmount {
  integerPart: string;
  /** Decimal separator + fraction digits together (e.g. `',56'`). */
  fractionPart: string;
  currencySymbol: string;
}

/**
 * Splits an already-non-negative Euro amount into integer/fraction/currency
 * pieces via `Intl.NumberFormat#formatToParts` — locale-correct grouping and
 * decimal separators without re-implementing number formatting. The €-symbol
 * is always re-attached AFTER the number by the caller (spec §3a), regardless
 * of where the locale would normally place it.
 */
function splitAmountParts(absEuros: number, localeTag: string, currency: string): SplitAmount {
  const parts = new Intl.NumberFormat(localeTag, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(absEuros);

  let integerPart = '';
  let fractionPart = '';
  let currencySymbol = '€';
  let seenDecimal = false;

  for (const part of parts) {
    switch (part.type) {
      case 'currency':
        currencySymbol = part.value;
        break;
      case 'decimal':
        seenDecimal = true;
        fractionPart += part.value;
        break;
      case 'fraction':
        fractionPart += part.value;
        break;
      case 'integer':
      case 'group':
        if (!seenDecimal) integerPart += part.value;
        break;
      default:
        // literal/percentSign/etc. — dropped, spacing/position is handled manually.
        break;
    }
  }

  return { integerPart, fractionPart, currencySymbol };
}

/** Resolves the active `Intl` locale tag from i18n, never hardcoded (spec §3a). */
function resolveLocaleTag(language: string | undefined): string {
  const shortCode = language?.split('-')[0] ?? 'de';
  return DATE_LOCALES[shortCode] ?? 'de-DE';
}

/**
 * `Amount` — see {@link AmountProps} for the full contract.
 *
 * @example
 * <Amount cents={123456} />                          // "1.234,56 €"
 * <Amount cents={-500} size="display" tone="danger" /> // "−5,00 €", cents de-emphasized
 * <Amount cents={0} />                                 // "0,00 €" (no dash — caller decides dash semantics)
 */
export function Amount({
  cents,
  currency = 'EUR',
  size = 'md',
  tone = 'default',
  deemphasizeCents,
  className = '',
  'aria-label': ariaLabelProp,
}: AmountProps) {
  const { i18n } = useTranslation();
  const localeTag = resolveLocaleTag(i18n?.language);

  // Rounding/cent-math delegated to moneyUtils — throws on non-finite/non-integer
  // input, which is the intended fail-loud behavior (caller bug, not swallowed).
  const euros = centsToEuros(cents);
  const isNegative = euros < 0;
  const { integerPart, fractionPart, currencySymbol } = splitAmountParts(Math.abs(euros), localeTag, currency);

  const shouldDeemphasize = deemphasizeCents ?? size === 'display';

  const fullAriaLabel = ariaLabelProp ?? formatCurrency(euros, currency, localeTag);

  const outerClasses = ['font-data', 'tabular-nums', SIZE_CLASSES[size], TONE_CLASSES[tone], className]
    .filter(Boolean)
    .join(' ');

  const centsClasses = shouldDeemphasize ? 'text-[0.7em] text-muted' : '';

  return (
    <span className={outerClasses} aria-label={fullAriaLabel}>
      <span aria-hidden="true">
        {isNegative && REAL_MINUS}
        {integerPart}
        <span className={centsClasses || undefined}>{fractionPart}</span>
        <span className="text-muted">{NARROW_SPACE}{currencySymbol}</span>
      </span>
    </span>
  );
}

export default Amount;
